import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Navigate, useSearchParams } from "react-router-dom";

import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";

import InfoAlert from "../../common/InfoAlert";

import useInfoAlert from "../../../hooks/useInfoAlert";

import { useLoader } from "../../../context/LoaderContext";

import { useUserData } from "../../../hooks/useUserData";

import {

  parsePermissions,

  hasPermission,

  getAllPermissionKeys,

  isSystemAdministratorRole,

} from "../../../utils/permissionUtils";

import { fetchQmsConfig, saveQmsConfig } from "../qms/qmsService";

import CapaActionPlanningEmailConfigEditor from "../capa/CapaActionPlanningEmailConfigEditor";
import QmsLibraryAutoAssignConfig from "./QmsLibraryAutoAssignConfig";
import QmsDocumentTypes from "../qms/QmsDocumentTypes";
import QmsDocumentCategories from "../qms/QmsDocumentCategories";
import { organizationHasQmsModule } from "../../../utils/orgModuleAccess";

import {
  QMS_LIBRARY_STAFF_ASSIGN_EMAIL_MERGE_TAGS,
  QMS_LIBRARY_STAFF_REMOVE_EMAIL_MERGE_TAGS,
} from "../qms/qmsLibraryStaffEmailTags";
import {
  QMS_MY_LEARNING_STAFF_ASSIGN_EMAIL_MERGE_TAGS,
  QMS_MY_LEARNING_STAFF_REMOVE_EMAIL_MERGE_TAGS,
} from "../qms/qmsMyLearningStaffEmailTags";
import {
  QMS_VAULT_APPROVE_EMAIL_MERGE_TAGS,
  QMS_VAULT_REVIEW_EMAIL_MERGE_TAGS,
} from "../qms/qmsVaultWorkflowEmailTags";
import {
  QMS_TRANSFER_OWNERSHIP_EMAIL_MERGE_TAGS,
} from "../qms/qmsTransferOwnershipEmailTags";
import {
  QMS_REMOVE_EMAIL_LINK_BLOCK_REASON,
  validateAssignEmailBody,
  validateAssignEmailSubject,
  validateRemoveEmailBody,
  validateRemoveEmailSubject,
  validateTransferOwnershipEmailBody,
  validateTransferOwnershipEmailSubject,
} from "../qms/qmsLibraryStaffEmailValidation";

import "./CapaConfigMaster.scss";



const SECTIONS = [

  {

    key: "documentTypes",

    label: "Document Type",

    subtitle: "QMS document type master values",

    icon: "fas fa-tag",

    permissionKeys: ["QMS_CONFIGURE"],

  },

  {

    key: "documentCategories",

    label: "Document Category",

    subtitle: "QMS document category master values",

    icon: "fas fa-folder-tree",

    permissionKeys: ["QMS_CONFIGURE"],

  },

  {

    key: "libraryAutoAssign",

    label: "Library auto-assign",

    subtitle: "Auto-assign published documents to eligible users",

    icon: "fas fa-book-open",

    permissionKeys: ["QMS_CONFIGURE"],

  },

  {

    key: "libraryStaffAssignEmail",

    label: "Library staff — assign",

    subtitle: "Email when staff are added to a document library",

    icon: "fas fa-user-plus",

  },

  {

    key: "libraryStaffRemoveEmail",

    label: "Library staff — remove",

    subtitle: "Email when staff access is removed",

    icon: "fas fa-user-minus",

  },

  {

    key: "myLearningStaffAssignEmail",

    label: "My Learning — assign",

    subtitle: "Email when staff are assigned to My Learning",

    icon: "fas fa-graduation-cap",

  },

  {

    key: "myLearningStaffRemoveEmail",

    label: "My Learning — remove",

    subtitle: "Email when My Learning access is removed",

    icon: "fas fa-user-slash",

  },

  {

    key: "vaultReviewEmail",

    label: "My Vault — send for review",

    subtitle: "Email when a document is sent to reviewers",

    icon: "fas fa-user-check",

  },

  {

    key: "vaultApproveEmail",

    label: "My Vault — send for approval",

    subtitle: "Email when a document is sent to approvers",

    icon: "fas fa-stamp",

  },

  {

    key: "transferOwnershipEmail",

    label: "Document Control — transfer ownership",

    subtitle: "Email sent to the new owner when documents are transferred",

    icon: "fas fa-exchange-alt",

  },

];



function sectionVisible(section, userPermissions, isSystemAdmin) {
  if (isSystemAdmin) return true;
  const keys = section.permissionKeys || ["QMS_CONFIGURE"];
  return keys.some((key) => hasPermission(userPermissions, key));
}



function applyQmsConfig(cfg, setters) {

  const assignSubjectDisplay = String(

    cfg?.libraryStaffAssignEmailSubjectDisplay ||

      cfg?.libraryStaffAssignEmailSubjectTemplate ||

      cfg?.libraryStaffAssignEmailSubjectDefault ||

      "",

  ).trim();

  const assignBodyDisplay = String(

    cfg?.libraryStaffAssignEmailBodyDisplay ||

      cfg?.libraryStaffAssignEmailBodyTemplate ||

      cfg?.libraryStaffAssignEmailBodyDefault ||

      "",

  ).trim();

  const removeSubjectDisplay = String(

    cfg?.libraryStaffRemoveEmailSubjectDisplay ||

      cfg?.libraryStaffRemoveEmailSubjectTemplate ||

      cfg?.libraryStaffRemoveEmailSubjectDefault ||

      "",

  ).trim();

  const removeBodyDisplay = String(

    cfg?.libraryStaffRemoveEmailBodyDisplay ||

      cfg?.libraryStaffRemoveEmailBodyTemplate ||

      cfg?.libraryStaffRemoveEmailBodyDefault ||

      "",

  ).trim();

  const reviewSubjectDisplay = String(
    cfg?.reviewWorkflowEmailSubjectDisplay ||
      cfg?.reviewWorkflowEmailSubjectTemplate ||
      cfg?.reviewWorkflowEmailSubjectDefault ||
      "",
  ).trim();

  const reviewBodyDisplay = String(
    cfg?.reviewWorkflowEmailBodyDisplay ||
      cfg?.reviewWorkflowEmailBodyTemplate ||
      cfg?.reviewWorkflowEmailBodyDefault ||
      "",
  ).trim();

  const approveSubjectDisplay = String(
    cfg?.approveWorkflowEmailSubjectDisplay ||
      cfg?.approveWorkflowEmailSubjectTemplate ||
      cfg?.approveWorkflowEmailSubjectDefault ||
      "",
  ).trim();

  const approveBodyDisplay = String(
    cfg?.approveWorkflowEmailBodyDisplay ||
      cfg?.approveWorkflowEmailBodyTemplate ||
      cfg?.approveWorkflowEmailBodyDefault ||
      "",
  ).trim();

  const mlAssignSubjectDisplay = String(
    cfg?.myLearningStaffAssignEmailSubjectDisplay ||
      cfg?.myLearningStaffAssignEmailSubjectTemplate ||
      cfg?.myLearningStaffAssignEmailSubjectDefault ||
      "",
  ).trim();

  const mlAssignBodyDisplay = String(
    cfg?.myLearningStaffAssignEmailBodyDisplay ||
      cfg?.myLearningStaffAssignEmailBodyTemplate ||
      cfg?.myLearningStaffAssignEmailBodyDefault ||
      "",
  ).trim();

  const mlRemoveSubjectDisplay = String(
    cfg?.myLearningStaffRemoveEmailSubjectDisplay ||
      cfg?.myLearningStaffRemoveEmailSubjectTemplate ||
      cfg?.myLearningStaffRemoveEmailSubjectDefault ||
      "",
  ).trim();

  const mlRemoveBodyDisplay = String(
    cfg?.myLearningStaffRemoveEmailBodyDisplay ||
      cfg?.myLearningStaffRemoveEmailBodyTemplate ||
      cfg?.myLearningStaffRemoveEmailBodyDefault ||
      "",
  ).trim();

  const transferSubjectDisplay = String(
    cfg?.transferOwnershipEmailSubjectDisplay ||
      cfg?.transferOwnershipEmailSubjectTemplate ||
      cfg?.transferOwnershipEmailSubjectDefault ||
      "",
  ).trim();

  const transferBodyDisplay = String(
    cfg?.transferOwnershipEmailBodyDisplay ||
      cfg?.transferOwnershipEmailBodyTemplate ||
      cfg?.transferOwnershipEmailBodyDefault ||
      "",
  ).trim();

  setters.setAssignSubject(assignSubjectDisplay);

  setters.setAssignBody(assignBodyDisplay);

  setters.setRemoveSubject(removeSubjectDisplay);

  setters.setRemoveBody(removeBodyDisplay);

  setters.setReviewSubject(reviewSubjectDisplay);

  setters.setReviewBody(reviewBodyDisplay);

  setters.setApproveSubject(approveSubjectDisplay);

  setters.setApproveBody(approveBodyDisplay);

  setters.setMlAssignSubject(mlAssignSubjectDisplay);

  setters.setMlAssignBody(mlAssignBodyDisplay);

  setters.setMlRemoveSubject(mlRemoveSubjectDisplay);

  setters.setMlRemoveBody(mlRemoveBodyDisplay);

  setters.setTransferSubject(transferSubjectDisplay);

  setters.setTransferBody(transferBodyDisplay);

  setters.setLibraryAutoAssignEnabled?.(
    cfg?.libraryAutoAssignEnabled === true || cfg?.libraryAutoAssignEnabled === 1,
  );

  setters.setSavedSnapshot(

    JSON.stringify({

      assignSubject: assignSubjectDisplay,

      assignBody: assignBodyDisplay,

      removeSubject: removeSubjectDisplay,

      removeBody: removeBodyDisplay,

      reviewSubject: reviewSubjectDisplay,

      reviewBody: reviewBodyDisplay,

      approveSubject: approveSubjectDisplay,

      approveBody: approveBodyDisplay,

      mlAssignSubject: mlAssignSubjectDisplay,

      mlAssignBody: mlAssignBodyDisplay,

      mlRemoveSubject: mlRemoveSubjectDisplay,

      mlRemoveBody: mlRemoveBodyDisplay,

      transferSubject: transferSubjectDisplay,

      transferBody: transferBodyDisplay,

    }),

  );

}



const QmsConfigMaster = () => {

  const { userData } = useUserData();

  const [searchParams] = useSearchParams();

  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const { showLoader, hideLoader } = useLoader();

  const [loading, setLoading] = useState(true);

  const [reloading, setReloading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [activeSection, setActiveSection] = useState("documentTypes");

  const [assignSubject, setAssignSubject] = useState("");

  const [assignBody, setAssignBody] = useState("");

  const [removeSubject, setRemoveSubject] = useState("");

  const [removeBody, setRemoveBody] = useState("");

  const [reviewSubject, setReviewSubject] = useState("");

  const [reviewBody, setReviewBody] = useState("");

  const [approveSubject, setApproveSubject] = useState("");

  const [approveBody, setApproveBody] = useState("");

  const [mlAssignSubject, setMlAssignSubject] = useState("");

  const [mlAssignBody, setMlAssignBody] = useState("");

  const [mlRemoveSubject, setMlRemoveSubject] = useState("");

  const [mlRemoveBody, setMlRemoveBody] = useState("");

  const [transferSubject, setTransferSubject] = useState("");

  const [transferBody, setTransferBody] = useState("");

  const [libraryAutoAssignEnabled, setLibraryAutoAssignEnabled] = useState(false);

  const [savedSnapshot, setSavedSnapshot] = useState("");

  const isSystemAdmin = isSystemAdministratorRole(userData?.Role);

  const userPermissions = isSystemAdmin

    ? getAllPermissionKeys()

    : parsePermissions(userData?.Permission || "");

  const canConfigureQms =

    isSystemAdmin || hasPermission(userPermissions, "QMS_CONFIGURE");

  const visibleSections = useMemo(

    () => SECTIONS.filter((section) => sectionVisible(section, userPermissions, isSystemAdmin)),

    [userPermissions, isSystemAdmin],

  );

  const canAccessQmsConfig = useMemo(() => {

    if (!userData) return false;

    if (isSystemAdmin) return true;

    if (!organizationHasQmsModule(userData)) return false;

    return canConfigureQms;

  }, [userData, isSystemAdmin, canConfigureQms]);



  useEffect(() => {

    const section = searchParams.get("section");

    if (section && visibleSections.some((item) => item.key === section)) {

      setActiveSection(section);

    }

  }, [searchParams, visibleSections]);



  useEffect(() => {

    if (

      visibleSections.length > 0 &&

      !visibleSections.some((section) => section.key === activeSection)

    ) {

      setActiveSection(visibleSections[0].key);

    }

  }, [visibleSections, activeSection]);



  const currentSnapshot = useMemo(

    () =>

      JSON.stringify({

        assignSubject,

        assignBody,

        removeSubject,

        removeBody,

        reviewSubject,

        reviewBody,

        approveSubject,

        approveBody,

        mlAssignSubject,

        mlAssignBody,

        mlRemoveSubject,

        mlRemoveBody,

        transferSubject,

        transferBody,

      }),

    [assignSubject, assignBody, removeSubject, removeBody, reviewSubject, reviewBody, approveSubject, approveBody, mlAssignSubject, mlAssignBody, mlRemoveSubject, mlRemoveBody, transferSubject, transferBody],

  );

  const hasUnsavedChanges =

    Boolean(savedSnapshot) && savedSnapshot !== currentSnapshot;



  const loadConfig = async ({ showBlockingLoader = false, useGlobalLoader = true } = {}) => {

    if (!userData?.OrganizationId) return;

    if (showBlockingLoader) {

      setLoading(true);

    } else {

      setReloading(true);

    }

    if (useGlobalLoader) {

      showLoader("Loading QMS configuration...");

    }

    try {

      const cfg = await fetchQmsConfig(userData);

      applyQmsConfig(cfg, {

        setAssignSubject,

        setAssignBody,

        setRemoveSubject,

        setRemoveBody,

        setReviewSubject,

        setReviewBody,

        setApproveSubject,

        setApproveBody,

        setMlAssignSubject,

        setMlAssignBody,

        setMlRemoveSubject,

        setMlRemoveBody,

        setTransferSubject,

        setTransferBody,

        setLibraryAutoAssignEnabled,

        setSavedSnapshot,

      });

    } catch (_e) {

      showAlert({

        title: "QMS Config",

        message: "Unable to load QMS config.",

        type: "danger",

      });

    } finally {

      if (useGlobalLoader) {

        hideLoader();

      }

      if (showBlockingLoader) {

        setLoading(false);

      } else {

        setReloading(false);

      }

    }

  };



  useEffect(() => {

    loadConfig({ showBlockingLoader: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [userData?.OrganizationId, userData?.StaffId]);



  const handleSave = async (scopeLabel = "QMS configuration") => {

    if (!userData?.OrganizationId) return;

    const assignSubjectCheck = validateAssignEmailSubject(assignSubject);
    const assignBodyCheck = validateAssignEmailBody(assignBody);
    const removeSubjectCheck = validateRemoveEmailSubject(removeSubject);
    const removeBodyCheck = validateRemoveEmailBody(removeBody);
    const reviewSubjectCheck = validateAssignEmailSubject(reviewSubject);
    const reviewBodyCheck = validateAssignEmailBody(reviewBody);
    const approveSubjectCheck = validateAssignEmailSubject(approveSubject);
    const approveBodyCheck = validateAssignEmailBody(approveBody);
    const mlAssignSubjectCheck = validateAssignEmailSubject(mlAssignSubject);
    const mlAssignBodyCheck = validateAssignEmailBody(mlAssignBody);
    const mlRemoveSubjectCheck = validateRemoveEmailSubject(mlRemoveSubject);
    const mlRemoveBodyCheck = validateRemoveEmailBody(mlRemoveBody);
    const transferSubjectCheck = validateTransferOwnershipEmailSubject(transferSubject);
    const transferBodyCheck = validateTransferOwnershipEmailBody(transferBody);
    if (!assignSubjectCheck.allowed || !assignBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message: assignSubjectCheck.message || assignBodyCheck.message || "Assign email template is invalid.",
        type: "warning",
      });
      return;
    }
    if (!removeSubjectCheck.allowed || !removeBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          removeSubjectCheck.message || removeBodyCheck.message || QMS_REMOVE_EMAIL_LINK_BLOCK_REASON,
        type: "warning",
      });
      return;
    }
    if (!reviewSubjectCheck.allowed || !reviewBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          reviewSubjectCheck.message || reviewBodyCheck.message || "Send for review email template is invalid.",
        type: "warning",
      });
      return;
    }
    if (!approveSubjectCheck.allowed || !approveBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          approveSubjectCheck.message || approveBodyCheck.message || "Send for approval email template is invalid.",
        type: "warning",
      });
      return;
    }
    if (!mlAssignSubjectCheck.allowed || !mlAssignBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          mlAssignSubjectCheck.message || mlAssignBodyCheck.message || "My Learning assign email template is invalid.",
        type: "warning",
      });
      return;
    }
    if (!mlRemoveSubjectCheck.allowed || !mlRemoveBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          mlRemoveSubjectCheck.message || mlRemoveBodyCheck.message || QMS_REMOVE_EMAIL_LINK_BLOCK_REASON,
        type: "warning",
      });
      return;
    }
    if (!transferSubjectCheck.allowed || !transferBodyCheck.allowed) {
      showAlert({
        title: "QMS Config",
        message:
          transferSubjectCheck.message ||
          transferBodyCheck.message ||
          "Transfer ownership email template is invalid.",
        type: "warning",
      });
      return;
    }

    showLoader(`Saving ${scopeLabel}...`);

    setSaving(true);

    try {

      const result = await saveQmsConfig(

        {

          libraryStaffAssignEmailSubjectTemplate: assignSubject,

          libraryStaffAssignEmailBodyTemplate: assignBody,

          libraryStaffRemoveEmailSubjectTemplate: removeSubject,

          libraryStaffRemoveEmailBodyTemplate: removeBody,

          reviewWorkflowEmailSubjectTemplate: reviewSubject,

          reviewWorkflowEmailBodyTemplate: reviewBody,

          approveWorkflowEmailSubjectTemplate: approveSubject,

          approveWorkflowEmailBodyTemplate: approveBody,

          myLearningStaffAssignEmailSubjectTemplate: mlAssignSubject,

          myLearningStaffAssignEmailBodyTemplate: mlAssignBody,

          myLearningStaffRemoveEmailSubjectTemplate: mlRemoveSubject,

          myLearningStaffRemoveEmailBodyTemplate: mlRemoveBody,

          transferOwnershipEmailSubjectTemplate: transferSubject,

          transferOwnershipEmailBodyTemplate: transferBody,

        },

        userData,

      );

      if (!result.success) {

        throw new Error(result.message || "Save failed.");

      }

      showAlert({

        title: "QMS Config",

        message: result.message || `${scopeLabel} saved successfully.`,

        type: "success",

      });

      await loadConfig({ showBlockingLoader: false, useGlobalLoader: false });

    } catch (e) {

      showAlert({

        title: "QMS Config",

        message: e?.message || "Failed to save QMS config.",

        type: "danger",

      });

    } finally {

      hideLoader();

      setSaving(false);

    }

  };



  const handleRemoveForbiddenContent = useCallback(({ message }) => {
    showAlert({
      title: "Remove email template",
      message: message || QMS_REMOVE_EMAIL_LINK_BLOCK_REASON,
      type: "warning",
    });
  }, [showAlert]);

  const handleAssignForbiddenContent = useCallback(({ message }) => {
    showAlert({
      title: "Assign email template",
      message: message || "This content is not allowed in the assign email template.",
      type: "warning",
    });
  }, [showAlert]);

  if (!userData) {

    return (

      <div className="d-flex justify-content-center p-5">

        <div className="spinner-border" role="status" />

      </div>

    );

  }



  if (!canAccessQmsConfig) {

    return <Navigate to="/notification-center" replace />;

  }



  return (

    <div className="user-mgmt-page capa-config-saas">

      <PageBreadcrumb

        items={[{ label: "Home", path: "/notification-center" }, { label: "QMS Config" }]}

        actions={[

          {

            icon: "fas fa-refresh",

            label: reloading ? "Refreshing..." : "Refresh",

            onClick: () => loadConfig({ showBlockingLoader: false }),

          },

        ]}

      />

      <InfoAlert {...alertState} onClose={hideAlert} />

      {loading ? (

        <div className="card shadow-sm">

          <div className="card-body text-muted">Loading QMS configuration...</div>

        </div>

      ) : (

        <div className="row g-3 capa-config-saas__layout">

          <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">

            <div className="card capa-config-saas__nav-card">

              <div className="card-header fw-semibold">Configuration sections</div>

              <div className="list-group list-group-flush capa-config-saas__nav-list">

                {visibleSections.map((section) => (

                  <button

                    key={section.key}

                    type="button"

                    className={`list-group-item list-group-item-action capa-config-saas__nav-item ${

                      activeSection === section.key ? "active" : ""

                    }`}

                    onClick={() => setActiveSection(section.key)}

                    aria-current={activeSection === section.key ? "page" : undefined}

                  >

                    <div className="d-flex align-items-start gap-2">

                      <i className={`${section.icon} mt-1`} />

                      <div>

                        <div className="fw-semibold capa-config-saas__nav-item-title">

                          {section.label}

                        </div>

                        <div

                          className={`small capa-config-saas__nav-item-subtitle ${

                            activeSection === section.key ? "text-white-50" : "text-muted"

                          }`}

                        >

                          {section.subtitle}

                        </div>

                      </div>

                    </div>

                  </button>

                ))}

              </div>

            </div>

          </div>



          <div className="col-md-8 col-xl-9 capa-config-saas__content-col">

            {activeSection === "documentTypes" ? (

              <div className="card shadow-sm capa-config-saas__panel-card">

                <div className="card-body">

                  <QmsDocumentTypes embedded />

                </div>

              </div>

            ) : null}



            {activeSection === "documentCategories" ? (

              <div className="card shadow-sm capa-config-saas__panel-card">

                <div className="card-body">

                  <QmsDocumentCategories embedded />

                </div>

              </div>

            ) : null}



            {activeSection === "libraryAutoAssign" ? (

              <QmsLibraryAutoAssignConfig

                userData={userData}

                enabled={libraryAutoAssignEnabled}

                onEnabledChange={setLibraryAutoAssignEnabled}

                showAlert={showAlert}

                showLoader={showLoader}

                hideLoader={hideLoader}

              />

            ) : null}



            {activeSection === "libraryStaffAssignEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">Document library — staff assigned</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={assignSubject}

                      onSubjectChange={setAssignSubject}

                      bodyTemplate={assignBody}

                      onBodyChange={setAssignBody}

                      mergeTags={QMS_LIBRARY_STAFF_ASSIGN_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      validateSubjectContent={validateAssignEmailSubject}

                      validateBodyContent={validateAssignEmailBody}

                      onForbiddenContent={handleAssignForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("Library staff assign email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "libraryStaffRemoveEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">Document library — staff removed</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={removeSubject}

                      onSubjectChange={setRemoveSubject}

                      bodyTemplate={removeBody}

                      onBodyChange={setRemoveBody}

                      mergeTags={QMS_LIBRARY_STAFF_REMOVE_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      forbidDocumentLinks

                      validateSubjectContent={validateRemoveEmailSubject}

                      validateBodyContent={validateRemoveEmailBody}

                      onForbiddenContent={handleRemoveForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("Library staff remove email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "myLearningStaffAssignEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">My Learning — staff assigned</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={mlAssignSubject}

                      onSubjectChange={setMlAssignSubject}

                      bodyTemplate={mlAssignBody}

                      onBodyChange={setMlAssignBody}

                      mergeTags={QMS_MY_LEARNING_STAFF_ASSIGN_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      validateSubjectContent={validateAssignEmailSubject}

                      validateBodyContent={validateAssignEmailBody}

                      onForbiddenContent={handleAssignForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("My Learning assign email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "myLearningStaffRemoveEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">My Learning — staff removed</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={mlRemoveSubject}

                      onSubjectChange={setMlRemoveSubject}

                      bodyTemplate={mlRemoveBody}

                      onBodyChange={setMlRemoveBody}

                      mergeTags={QMS_MY_LEARNING_STAFF_REMOVE_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      forbidDocumentLinks

                      validateSubjectContent={validateRemoveEmailSubject}

                      validateBodyContent={validateRemoveEmailBody}

                      onForbiddenContent={handleRemoveForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("My Learning remove email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "vaultReviewEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">My Vault — send for review</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={reviewSubject}

                      onSubjectChange={setReviewSubject}

                      bodyTemplate={reviewBody}

                      onBodyChange={setReviewBody}

                      mergeTags={QMS_VAULT_REVIEW_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      validateSubjectContent={validateAssignEmailSubject}

                      validateBodyContent={validateAssignEmailBody}

                      onForbiddenContent={handleAssignForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("Send for review email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "vaultApproveEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">My Vault — send for approval</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={approveSubject}

                      onSubjectChange={setApproveSubject}

                      bodyTemplate={approveBody}

                      onBodyChange={setApproveBody}

                      mergeTags={QMS_VAULT_APPROVE_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      validateSubjectContent={validateAssignEmailSubject}

                      validateBodyContent={validateAssignEmailBody}

                      onForbiddenContent={handleAssignForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("Send for approval email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}



            {activeSection === "transferOwnershipEmail" ? (

              <div className="card shadow-sm capa-config-saas__panel-card capa-ap-email-panel-card">

                <div className="card-body capa-ap-email-panel-body">

                  {hasUnsavedChanges ? (

                    <div className="alert alert-warning py-2 px-3 mb-3 flex-shrink-0">

                      <i className="fas fa-exclamation-triangle me-2" />

                      You have unsaved changes. Click <strong>Save</strong> to apply updates.

                    </div>

                  ) : null}

                  <h6 className="mb-2 flex-shrink-0">Document Control — ownership transferred</h6>

                  <div className="capa-ap-email-editor-grow">

                    <CapaActionPlanningEmailConfigEditor

                      subjectTemplate={transferSubject}

                      onSubjectChange={setTransferSubject}

                      bodyTemplate={transferBody}

                      onBodyChange={setTransferBody}

                      mergeTags={QMS_TRANSFER_OWNERSHIP_EMAIL_MERGE_TAGS}

                      fillAvailableHeight

                      validateSubjectContent={validateTransferOwnershipEmailSubject}

                      validateBodyContent={validateTransferOwnershipEmailBody}

                      onForbiddenContent={handleAssignForbiddenContent}

                    />

                  </div>

                  <div className="capa-ap-email-panel-footer d-flex justify-content-end flex-shrink-0">

                    <button

                      type="button"

                      className="btn btn-primary btn-sm"

                      onClick={() => handleSave("Transfer ownership email templates")}

                      disabled={saving || reloading}

                    >

                      {saving || reloading

                        ? saving

                          ? "Saving..."

                          : "Loading..."

                        : "Save email templates"}

                    </button>

                  </div>

                </div>

              </div>

            ) : null}

          </div>

        </div>

      )}

    </div>

  );

};



export default QmsConfigMaster;


